import React from 'react';
import { Box, Skeleton, Stack, Typography } from '@mui/material';
import PersonAddAlt1Rounded from '@mui/icons-material/PersonAddAlt1Rounded';
import MailRounded from '@mui/icons-material/MailRounded';
import ScheduleRounded from '@mui/icons-material/ScheduleRounded';
import PhoneForwardedRounded from '@mui/icons-material/PhoneForwardedRounded';
import CallEndRounded from '@mui/icons-material/CallEndRounded';
import PhoneMissedRounded from '@mui/icons-material/PhoneMissedRounded';
import RingVolumeRounded from '@mui/icons-material/RingVolumeRounded';
import PhoneInTalkRounded from '@mui/icons-material/PhoneInTalkRounded';
import EventAvailableRounded from '@mui/icons-material/EventAvailableRounded';
import AssignmentRounded from '@mui/icons-material/AssignmentRounded';
import PanToolRounded from '@mui/icons-material/PanToolRounded';
import ErrorOutlineRounded from '@mui/icons-material/ErrorOutlineRounded';
import DoNotDisturbOnRounded from '@mui/icons-material/DoNotDisturbOnRounded';
import OpenInNewRounded from '@mui/icons-material/OpenInNewRounded';
import {
  EmptyState, Modal, Panel, StatusChip, Timeline, ToolbarButton, type TimelineStep,
} from '../../../ui-kit';
import { fetchStlLead, type StlCallRecord, type StlLeadDetail } from '../../../services/speedToLeadService';
import { lengthOf, useLive, when } from '../rocco/live';
import { liveChip } from './status';

const MARKERS: Record<string, React.ReactNode> = {
  arrived: <PersonAddAlt1Rounded />,
  email: <MailRounded />,
  email_failed: <ErrorOutlineRounded />,
  call_set: <ScheduleRounded />,
  call: <PhoneForwardedRounded />,
  call_failed: <ErrorOutlineRounded />,
  no_answer: <PhoneMissedRounded />,
  call_ended: <CallEndRounded />,
  transfer: <RingVolumeRounded />,
  transferred: <PhoneInTalkRounded />,
  transfer_missed: <PhoneMissedRounded />,
  az_lead: <AssignmentRounded />,
  az_meeting: <EventAvailableRounded />,
  az_task: <AssignmentRounded />,
  az_problem: <ErrorOutlineRounded />,
  needs_person: <PanToolRounded />,
  closed: <DoNotDisturbOnRounded />,
};

const TONES: Record<string, TimelineStep['tone']> = {
  transferred: 'good',
  az_meeting: 'good',
  email_failed: 'warning',
  call_failed: 'warning',
  transfer_missed: 'warning',
  az_problem: 'warning',
  needs_person: 'warning',
};

/**
 * One lead, start to finish: arrived from HubSpot, each email, each call with
 * its transcript, the hand-over or the booking in AgencyZoom. Watches the lead
 * while open, so a call that ends while you read it appears in place.
 */
export default function LeadStoryModal({ leadId, onClose }: { leadId: string; onClose: () => void }) {
  const fetcher = React.useCallback(() => fetchStlLead(leadId), [leadId]);
  const { data: found, error } = useLive(fetcher, 4000);

  return (
    <Modal
      open
      onClose={onClose}
      title={found ? found.name : 'Loading…'}
      subtitle={found ? `Arrived from HubSpot ${when(found.arrived_at)}` : undefined}
      actions={found ? (
        <Stack direction="row" alignItems="center" spacing={1}>
          {found.agencyzoom_url ? (
            <ToolbarButton variant="ghost" icon={<OpenInNewRounded />}
              onClick={() => window.open(found.agencyzoom_url ?? '', '_blank', 'noopener')}>
              Open AgencyZoom
            </ToolbarButton>
          ) : null}
          <StatusChip {...liveChip(found)} />
        </Stack>
      ) : undefined}
      size="lg"
      footer={<ToolbarButton variant="ghost" onClick={onClose}>Close</ToolbarButton>}
    >
      {error && !found ? <EmptyState message={error} /> : null}
      {!found && !error ? <Loading /> : null}
      {found ? <Body found={found} /> : null}
    </Modal>
  );
}

function Body({ found }: { found: StlLeadDetail }) {
  const facts = [
    { label: 'Phone', value: found.phone_full ?? 'None in HubSpot' },
    { label: 'Email', value: found.email ?? 'None in HubSpot' },
    { label: 'Calls made', value: String(found.calls) },
    { label: 'In AgencyZoom with', value: found.specialist_name ?? 'Not yet' },
    { label: 'Booked for', value: found.callback_at ? when(found.callback_at) : 'Nothing booked' },
    { label: 'HubSpot contact', value: String(found.hubspot_contact_id) },
  ];
  return (
    <Stack spacing={2}>
      {found.status === 'needs_person' && found.needs_person_reason ? (
        <Box sx={(t) => ({
          p: 1.25, borderRadius: `${t.ds.radius.sm}px`, bgcolor: t.tokens.semantic.warningSoft, color: t.tokens.text.primary,
        })}>
          <Typography variant="body2"><strong>Needs a person:</strong> {found.needs_person_reason}</Typography>
        </Box>
      ) : null}
      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, alignItems: 'start' }}>
        <Stack spacing={2}>
          <Panel title="Calls" subtitle="What was said on each call">
            {found.call_records.length ? (
              <Stack spacing={2}>
                {found.call_records.map((c) => <CallRecord key={c.number} call={c} />)}
              </Stack>
            ) : (
              <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.tertiary })}>No calls yet.</Typography>
            )}
          </Panel>
        </Stack>
        <Stack spacing={2}>
          <Panel title="Lead">
            <Facts rows={facts} />
          </Panel>
          <Panel title="What happened" subtitle="Each step, as it happened">
            <Timeline steps={found.steps.map((step, index) => ({
              key: `${step.kind}-${index}`,
              title: step.title,
              meta: when(step.at),
              detail: step.detail ?? undefined,
              marker: MARKERS[step.kind] ?? <ScheduleRounded />,
              tone: TONES[step.kind],
            }))} />
          </Panel>
        </Stack>
      </Box>
    </Stack>
  );
}

function CallRecord({ call }: { call: StlCallRecord }) {
  const handOver = call.transfer_taken_at ? 'Handed to the specialist'
    : call.transfer_missed_at ? 'Hand-over not answered'
      : call.transfer_started_at ? 'Ringing the specialist' : null;
  return (
    <Stack spacing={1}>
      <Stack direction="row" justifyContent="space-between" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
        <Typography variant="subtitle2" sx={(t) => ({ color: t.tokens.text.primary })}>
          {`Call ${call.number} · ${when(call.started_at)}`}
        </Typography>
        <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.tertiary })}>
          {[call.outcome ?? (call.status === 'settled' ? '' : 'Under way'), lengthOf(call.duration_secs)]
            .filter((x) => x && x !== '—').join(' · ')}
        </Typography>
      </Stack>
      {handOver ? (
        <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.secondary })}>{handOver}</Typography>
      ) : null}
      {call.summary ? (
        <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.secondary })}>{call.summary}</Typography>
      ) : null}
      {call.transcript.length ? (
        <Stack spacing={1} sx={(t) => ({ p: 1.5, borderRadius: `${t.ds.radius.md}px`, bgcolor: t.tokens.surface.sunken })}>
          {call.transcript.map((turn, i) => (
            <Stack key={i} spacing={0.25}>
              <Typography variant="overline"
                sx={(t) => ({ color: turn.role === 'agent' ? t.tokens.brand.primary : t.tokens.text.tertiary })}>
                {turn.role === 'agent' ? 'AI' : 'Lead'}
              </Typography>
              <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.primary, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' })}>
                {turn.message}
              </Typography>
            </Stack>
          ))}
        </Stack>
      ) : (
        <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.tertiary })}>
          {call.status === 'settled' ? 'Nothing was said.' : 'The words arrive when the call ends.'}
        </Typography>
      )}
    </Stack>
  );
}

/** Labels on the left, their values on the right. Both wrap. */
function Facts({ rows }: { rows: { label: string; value: string }[] }) {
  return (
    <Stack spacing={0.75}>
      {rows.map((fact) => (
        <Stack key={fact.label} direction="row" justifyContent="space-between" spacing={2}>
          <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.tertiary })}>{fact.label}</Typography>
          <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.primary, textAlign: 'right', overflowWrap: 'anywhere' })}>
            {fact.value}
          </Typography>
        </Stack>
      ))}
    </Stack>
  );
}

function Loading() {
  return (
    <Stack spacing={1.25} aria-hidden>
      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
        <Skeleton variant="rounded" height={220} />
        <Skeleton variant="rounded" height={220} />
      </Box>
    </Stack>
  );
}
